/**
 * Panorama peint du plan des pistes, sur canvas.
 *
 * 1. Grilles au pas du relief (4 m) : couverture des pistes, densité de
 *    forêt (même formule que la 3D), rochers, éclairage et couleur de base
 *    (neige blanche ombrée de bleu, lumière d'ouest en hauteur, creux
 *    assombris, crêtes éclaircies).
 * 2. Rendu par colonnes « voxel space » : chaque colonne d'écran parcourt le
 *    terrain du sud (proche) vers le nord (loin) ; un point n'est peint que
 *    s'il dépasse ce qui est déjà peint plus bas. On garde, pour chaque
 *    pixel, le z du terrain vu : c'est le tampon de profondeur qui masque
 *    ensuite les tracés cachés par le relief.
 * 3. Touches de pinceau le long de la ligne de pente, sapins en petites
 *    touches sous la limite des arbres, chalets du village, papier.
 *
 * Le calcul est découpé en tranches (moins de 14 ms) pour ne pas figer
 * l'interface ; un travail annulé s'arrête à la tranche suivante.
 */
import { createNoise2D, fbm, ridged, smoothstep, type BuildingKind, type GameMap, type Heightfield, type Vec2 } from '@snowchain/shared';
import { placeVillageHouses } from '../../render/village';
import type { Domain, View } from './view';

export interface PaintScene {
  hf: Heightfield;
  map: GameMap;
  view: View;
  domain: Domain;
  /** Pixels du canvas par pixel CSS (au plus 2). */
  ratio: number;
  pistes: { path: Vec2[]; width: number }[];
  lifts: { bottom: Vec2; top: Vec2 }[];
  buildings: { kind: BuildingKind; x: number; z: number }[];
}

export interface Painted {
  canvas: HTMLCanvasElement;
  /** z du terrain vu en chaque pixel du canvas (-1e9 : ciel). */
  depth: Float32Array;
  pw: number;
  ph: number;
  ratio: number;
  /** Durée de calcul (ms). */
  ms: number;
}

export interface PaintJob {
  cancelled: boolean;
}

export const SKY_DEPTH = -1e9;

/** Lumière : d'ouest, haute, légèrement vers l'observateur. */
const LIGHT = norm3(-0.74, 0.56, 0.36);

function norm3(x: number, y: number, z: number): [number, number, number] {
  const l = Math.hypot(x, y, z);
  return [x / l, y / l, z / l];
}

/** Palette de la neige selon l'éclairage t (0 ombre, 1 plein soleil). */
const SNOW: [number, number, number, number][] = [
  [0, 92, 120, 172],
  [0.4, 168, 190, 224],
  [0.72, 230, 237, 247],
  [1, 255, 255, 252],
];
const ROCK: [number, number, number, number][] = [
  [0, 64, 64, 78],
  [0.5, 122, 116, 116],
  [1, 190, 180, 166],
];

function ramp(stops: [number, number, number, number][], t: number, out: Float32Array, o: number): void {
  let a = stops[0] as [number, number, number, number];
  for (let i = 1; i < stops.length; i++) {
    const b = stops[i] as [number, number, number, number];
    if (t <= b[0] || i === stops.length - 1) {
      const k = Math.max(0, Math.min(1, (t - a[0]) / (b[0] - a[0])));
      out[o] = a[1] + (b[1] - a[1]) * k;
      out[o + 1] = a[2] + (b[2] - a[2]) * k;
      out[o + 2] = a[3] + (b[3] - a[3]) * k;
      return;
    }
    a = b;
  }
}

/** Hachage entier vers [0, 1). */
function hash(a: number, b: number, c = 0): number {
  let h = (a * 374761393 + b * 668265263 + c * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** Cède la main au navigateur si la tranche courante dépasse 14 ms. */
function makeBreather(job: PaintJob): () => Promise<boolean> {
  let last = performance.now();
  return async () => {
    if (performance.now() - last < 14) return !job.cancelled;
    await new Promise<void>((r) => setTimeout(r, 0));
    last = performance.now();
    return !job.cancelled;
  };
}

interface Grids {
  cov: Float32Array;
  wood: Float32Array;
  rock: Float32Array;
  lit: Float32Array;
  col: Float32Array;
  water: Uint8Array;
}

/** Couverture des pistes au pas du relief (1 sur la piste, 0 au-delà de 6 m du bord). */
function stampPistes(hf: Heightfield, pistes: PaintScene['pistes'], cov: Float32Array): void {
  const { res, cell, half } = hf;
  for (const p of pistes) {
    const r = p.width / 2;
    for (let i = 0; i < p.path.length - 1; i++) {
      const a = p.path[i] as Vec2;
      const b = p.path[i + 1] as Vec2;
      const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const n = Math.max(1, Math.ceil(len / 3));
      for (let k = 0; k <= n; k++) {
        const x = a[0] + ((b[0] - a[0]) * k) / n;
        const z = a[1] + ((b[1] - a[1]) * k) / n;
        const reach = r + 6;
        const i0 = Math.max(0, Math.floor((x - reach + half) / cell));
        const i1 = Math.min(res - 1, Math.ceil((x + reach + half) / cell));
        const j0 = Math.max(0, Math.floor((z - reach + half) / cell));
        const j1 = Math.min(res - 1, Math.ceil((z + reach + half) / cell));
        for (let j = j0; j <= j1; j++) {
          for (let ii = i0; ii <= i1; ii++) {
            const d = Math.hypot(-half + ii * cell - x, -half + j * cell - z);
            const c = d <= r ? 1 : d >= reach ? 0 : 1 - (d - r) / 6;
            const q = j * res + ii;
            if (c > (cov[q] as number)) cov[q] = c;
          }
        }
      }
    }
  }
}

/** Grilles de base : forêt, rochers, éclairage, couleur. */
async function buildGrids(sc: PaintScene, breathe: () => Promise<boolean>): Promise<Grids | null> {
  const { hf, map } = sc;
  const { res, cell, half } = hf;
  const n = res * res;
  const g: Grids = {
    cov: new Float32Array(n),
    wood: new Float32Array(n),
    rock: new Float32Array(n),
    lit: new Float32Array(n),
    col: new Float32Array(n * 3),
    water: new Uint8Array(n),
  };
  stampPistes(hf, sc.pistes, g.cov);
  const forest = createNoise2D(map.features.seed + 5);
  const rockNoise = createNoise2D(map.features.seed + 91);
  const v = map.features.village;
  const lake = map.features.lake;
  const at = (i: number, j: number) => hf.at(i, j);
  const tmp = new Float32Array(3);
  const rockCol = new Float32Array(3);
  for (let j = 0; j < res; j++) {
    const z = -half + j * cell;
    for (let i = 0; i < res; i++) {
      const x = -half + i * cell;
      const q = j * res + i;
      const h = at(i, j);
      const gx = (at(i + 1, j) - at(i - 1, j)) / (2 * cell);
      const gz = (at(i, j + 1) - at(i, j - 1)) / (2 * cell);
      const slope = Math.hypot(gx, gz);
      // Éclairage sur une normale exagérée (comme le relief affiché).
      const [nx, ny, nz] = norm3(-gx * 1.9, 1, -gz * 1.9);
      const diffuse = nx * (LIGHT[0] as number) + ny * (LIGHT[1] as number) + nz * (LIGHT[2] as number);
      // Creux et crêtes : écart à la moyenne d'un anneau de 20 m puis 48 m.
      const ring = (r: number) => (at(i + r, j) + at(i - r, j) + at(i, j + r) + at(i, j - r) + at(i + r, j + r) + at(i - r, j - r) + at(i + r, j - r) + at(i - r, j + r)) / 8;
      const concave = ring(5) - h + (ring(12) - h) * 0.5;
      let t = Math.max(0, Math.min(1, (diffuse + 0.3) / 1.3));
      t = Math.pow(t, 0.8) - Math.max(-0.12, Math.min(0.28, concave / 10));
      t = Math.max(0, Math.min(1, t));
      g.lit[q] = t;
      // Forêt : formule du décor 3D.
      const fn = fbm(forest, x / 420, z / 420, 3);
      const line = map.treeLine + fn * 45;
      let wood = h > line ? 0 : smoothstep(-0.2, 0.3, fn) * smoothstep(line, line - 70, h);
      if (Math.hypot(x - v.x, z - v.z) < v.radius * 0.85 || slope > 1.05 || (g.cov[q] as number) > 0.01) wood = 0;
      // Rochers : fortes pentes, plaques irrégulières.
      const rn = 0.5 + 0.5 * fbm(rockNoise, x / 70, z / 70, 3);
      let rock = smoothstep(0.62, 1.0, slope + (rn - 0.5) * 0.35) * smoothstep(60, 220, h);
      rock *= 1 - (g.cov[q] as number);
      const inLake = Math.hypot(x - lake.x, z - lake.z) < lake.radius * 1.25 && h <= hf.waterLevel + 0.4;
      if (inLake) wood = 0;
      g.wood[q] = wood;
      g.rock[q] = rock;
      ramp(SNOW, t, tmp, 0);
      let r = tmp[0] as number;
      let gg = tmp[1] as number;
      let b = tmp[2] as number;
      if (rock > 0) {
        ramp(ROCK, t * (0.75 + rn * 0.45), rockCol, 0);
        const k = rock * 0.92;
        r += ((rockCol[0] as number) - r) * k;
        gg += ((rockCol[1] as number) - gg) * k;
        b += ((rockCol[2] as number) - b) * k;
      }
      if (wood > 0) {
        // Sous-bois : neige bleutée et grisée entre les sapins.
        const k = wood * 0.6;
        r += (96 + t * 50 - r) * k;
        gg += (118 + t * 50 - gg) * k;
        b += (122 + t * 46 - b) * k;
      }
      const c = g.cov[q] as number;
      if (c > 0) {
        // Piste damée : neige lisse et claire.
        const k = c * 0.45;
        r += (246 - r) * k;
        gg += (249 - gg) * k;
        b += (253 - b) * k;
      }
      if (inLake) {
        g.water[q] = 1;
        const edge = Math.hypot(x - lake.x, z - lake.z) / (lake.radius * 1.25);
        r = 104 + edge * 40 + t * 18;
        gg = 156 + edge * 34 + t * 14;
        b = 198 + edge * 22 + t * 8;
      }
      const o = q * 3;
      g.col[o] = r;
      g.col[o + 1] = gg;
      g.col[o + 2] = b;
    }
    if ((j & 7) === 0 && !(await breathe())) return null;
  }
  return g;
}


/** Grille du relief hors carte (côtés) : montagnes inventées, raccordées au bord. */
interface FarGrid {
  x0: number;
  cell: number;
  nx: number;
  nz: number;
  h: Float32Array;
  col: Float32Array;
}

function buildFarGrid(sc: PaintScene, pw: number): FarGrid {
  const { hf, view: v, ratio } = sc;
  const half = hf.half;
  const cell = 12;
  const x0 = v.x0 - 2 * cell;
  const nx = Math.ceil(pw / (v.s * ratio) / cell) + 5;
  const nz = Math.ceil((2 * half) / cell) + 1;
  const n = createNoise2D(sc.map.features.seed + 57);
  const h = new Float32Array(nx * nz);
  for (let j = 0; j < nz; j++) {
    const z = -half + j * cell;
    const south = smoothstep(-500, 750, z);
    for (let i = 0; i < nx; i++) {
      const x = x0 + i * cell;
      const cx = x < -half ? -half : x > half ? half : x;
      const d = Math.abs(x - cx);
      const rid = ridged(n, x / 620, z / 620, 4);
      const peaks = (150 + 700 * rid) * (1 - south) + (18 + 70 * rid) * south;
      const base = hf.height(cx, z);
      h[j * nx + i] = base + (peaks - base) * smoothstep(0, 380, d);
    }
  }
  const col = new Float32Array(nx * nz * 3);
  const tmp = new Float32Array(3);
  const rockCol = new Float32Array(3);
  const at = (i: number, j: number) => h[Math.max(0, Math.min(nz - 1, j)) * nx + Math.max(0, Math.min(nx - 1, i))] as number;
  for (let j = 0; j < nz; j++) {
    for (let i = 0; i < nx; i++) {
      const gx = (at(i + 1, j) - at(i - 1, j)) / (2 * cell);
      const gz = (at(i, j + 1) - at(i, j - 1)) / (2 * cell);
      const [nx3, ny3, nz3] = norm3(-gx * 1.9, 1, -gz * 1.9);
      const diffuse = nx3 * (LIGHT[0] as number) + ny3 * (LIGHT[1] as number) + nz3 * (LIGHT[2] as number);
      const t = Math.max(0, Math.min(1, Math.pow(Math.max(0, (diffuse + 0.3) / 1.3), 0.8)));
      ramp(SNOW, t, tmp, 0);
      const rock = smoothstep(0.7, 1.1, Math.hypot(gx, gz)) * 0.8;
      ramp(ROCK, t, rockCol, 0);
      const o = (j * nx + i) * 3;
      for (let k = 0; k < 3; k++) col[o + k] = (tmp[k] as number) + ((rockCol[k] as number) - (tmp[k] as number)) * rock;
    }
  }
  return { x0, cell, nx, nz, h, col };
}

/** Couleur du ciel par ligne : bleu en haut, brume claire vers les crêtes. */
function skyRows(ph: number, horizon: number): Float32Array {
  const out = new Float32Array(ph * 3);
  for (let y = 0; y < ph; y++) {
    const t = Math.max(0, Math.min(1, y / Math.max(1, horizon)));
    const k = t * t;
    out[y * 3] = 132 + (232 - 132) * k;
    out[y * 3 + 1] = 170 + (236 - 170) * k;
    out[y * 3 + 2] = 208 + (240 - 208) * k;
  }
  return out;
}

/**
 * Rendu par colonnes avec tampon de profondeur. Au-delà du domaine (au
 * nord), le relief est plafonné sous une ligne de crêtes peinte juste
 * au-dessus du domaine : le ciel reste visible, comme sur un plan peint.
 */
async function marchColumns(sc: PaintScene, g: Grids, far: FarGrid, img: ImageData, depth: Float32Array, breathe: () => Promise<boolean>): Promise<boolean> {
  const { hf, view: v, ratio, domain } = sc;
  const { res, cell, half } = hf;
  const pw = img.width;
  const ph = img.height;
  const data = img.data;
  const S = v.s * ratio;
  const ec = v.e * v.cos;
  const crest = createNoise2D(sc.map.features.seed + 33);
  const grain = new Float32Array(8192);
  for (let k = 0; k < grain.length; k++) grain[k] = (hash(k, 7) - 0.5) * 7;
  // Ordonnée projetée du haut du domaine, puis ligne de crêtes au-dessus.
  let domTop = Infinity;
  for (let k = 0; k <= 40; k++) {
    const x = domain.minX + ((domain.maxX - domain.minX) * k) / 40;
    for (const z of [domain.minZ, domain.minZ + 120, domain.minZ + 260]) domTop = Math.min(domTop, z * v.sin - hf.height(x, z) * ec);
  }
  const skyBand = v.y0 + (v.h * 0.16) / v.s;
  const capBase = Math.min(domTop - 10 / v.s, skyBand + (domTop - skyBand) * 0.15);
  const capAmp = Math.max(0, capBase - (v.y0 + (v.h * 0.06) / v.s));
  const zFadeA = domain.minZ + 80;
  const zFadeB = domain.minZ - 260;
  const zStart = Math.min(half, (ph / S + v.y0 + hf.minH * ec) / v.sin + 8);
  const dz = Math.max(0.5, Math.min(4, 0.8 / (S * v.sin)));
  const sky = skyRows(ph, Math.max(1, (capBase - v.y0) * S));
  const col = g.col;
  for (let px = 0; px < pw; px++) {
    const x = v.x0 + (px + 0.5) / S;
    const cx = x < -half ? -half : x > half ? half : x;
    const out = Math.abs(x - cx);
    const fi = (cx + half) / cell;
    let i = Math.floor(fi);
    if (i >= res - 1) i = res - 2;
    const tx = fi - i;
    // Ligne de crêtes de cette colonne (m projetés).
    const cap = capBase - capAmp * (0.25 + 0.75 * ridged(crest, x / 520, 0.5, 3));
    // Bords de la carte fondus dans la brume, au-delà un relief inventé plus pâle.
    const hazeOut = smoothstep(-260, 320, Math.abs(x) - half) * 0.78;
    const ffx = Math.max(0, Math.min(far.nx - 1.001, (x - far.x0) / far.cell));
    const fi0 = Math.floor(ffx);
    const ftx = ffx - fi0;
    let ymin = ph;
    let pR = 0;
    let pG = 0;
    let pB = 0;
    let pY = ph;
    let pZ = zStart;
    let first = true;
    for (let z = zStart; z > -half; z -= dz) {
      let fz = (z + half) / cell;
      if (fz < 0) fz = 0;
      let j = Math.floor(fz);
      if (j >= res - 1) j = res - 2;
      const tz = fz - j;
      const q = j * res + i;
      const w00 = (1 - tx) * (1 - tz);
      const w10 = tx * (1 - tz);
      const w01 = (1 - tx) * tz;
      const w11 = tx * tz;
      const d = hf.data;
      let h: number;
      let r: number;
      let gg: number;
      let b: number;
      if (out > 0) {
        // Hors carte : grille lointaine.
        let ffz = (z + half) / far.cell;
        if (ffz > far.nz - 1.001) ffz = far.nz - 1.001;
        const fj = Math.floor(ffz);
        const ftz = ffz - fj;
        const fq = fj * far.nx + fi0;
        const a00 = (1 - ftx) * (1 - ftz);
        const a10 = ftx * (1 - ftz);
        const a01 = (1 - ftx) * ftz;
        const a11 = ftx * ftz;
        const fh = far.h;
        h = (fh[fq] as number) * a00 + (fh[fq + 1] as number) * a10 + (fh[fq + far.nx] as number) * a01 + (fh[fq + far.nx + 1] as number) * a11;
        const fo = fq * 3;
        const fo2 = fo + far.nx * 3;
        const fc = far.col;
        r = (fc[fo] as number) * a00 + (fc[fo + 3] as number) * a10 + (fc[fo2] as number) * a01 + (fc[fo2 + 3] as number) * a11;
        gg = (fc[fo + 1] as number) * a00 + (fc[fo + 4] as number) * a10 + (fc[fo2 + 1] as number) * a01 + (fc[fo2 + 4] as number) * a11;
        b = (fc[fo + 2] as number) * a00 + (fc[fo + 5] as number) * a10 + (fc[fo2 + 2] as number) * a01 + (fc[fo2 + 5] as number) * a11;
        // Raccord : près du bord, la couleur reprend celle de la carte.
        const kIn = 1 - smoothstep(0, 280, out);
        if (kIn > 0) {
          const o = q * 3;
          const o2 = o + res * 3;
          r += ((col[o] as number) * w00 + (col[o + 3] as number) * w10 + (col[o2] as number) * w01 + (col[o2 + 3] as number) * w11 - r) * kIn;
          gg += ((col[o + 1] as number) * w00 + (col[o + 4] as number) * w10 + (col[o2 + 1] as number) * w01 + (col[o2 + 4] as number) * w11 - gg) * kIn;
          b += ((col[o + 2] as number) * w00 + (col[o + 5] as number) * w10 + (col[o2 + 2] as number) * w01 + (col[o2 + 5] as number) * w11 - b) * kIn;
        }
      } else {
        h = (d[q] as number) * w00 + (d[q + 1] as number) * w10 + (d[q + res] as number) * w01 + (d[q + res + 1] as number) * w11;
        const o = q * 3;
        const o2 = o + res * 3;
        r = (col[o] as number) * w00 + (col[o + 3] as number) * w10 + (col[o2] as number) * w01 + (col[o2 + 3] as number) * w11;
        gg = (col[o + 1] as number) * w00 + (col[o + 4] as number) * w10 + (col[o2 + 1] as number) * w01 + (col[o2 + 4] as number) * w11;
        b = (col[o + 2] as number) * w00 + (col[o + 5] as number) * w10 + (col[o2 + 2] as number) * w01 + (col[o2 + 5] as number) * w11;
      }
      // Plafond des crêtes au-delà du domaine (raccord progressif).
      const wc = z < zFadeA ? smoothstep(zFadeA, zFadeB, z) : 0;
      if (wc > 0 || out > 0) {
        const hmax = (z * v.sin - cap) / ec;
        const capped = h > hmax ? hmax + (h - hmax) * 0.04 : h;
        const k = Math.max(wc, smoothstep(0, 200, out));
        h += (capped - h) * k;
      }
      // Brume : lointains et hors carte.
      const haze = Math.min(0.85, smoothstep(zFadeA + 200, zFadeB - 300, z) * 0.5 + hazeOut * 0.55 + smoothstep(500, -1000, z) * 0.08);
      if (haze > 0) {
        r += (222 - r) * haze;
        gg += (229 - gg) * haze;
        b += (238 - b) * haze;
      }
      const sy = (z * v.sin - h * ec - v.y0) * S;
      if (first) {
        pR = r;
        pG = gg;
        pB = b;
        pY = Math.max(sy, ph);
        first = false;
      }
      if (sy < ymin) {
        const top = sy <= 0 ? 0 : Math.ceil(sy);
        const span = pY - sy;
        for (let y = ymin - 1; y >= top; y -= 1) {
          let t = span > 1e-6 ? (y - sy) / span : 0;
          t = t < 0 ? 0 : t > 1 ? 1 : t;
          const k = (y * pw + px) * 4;
          const gr = grain[(px * 73 + y * 151) & 8191] as number;
          data[k] = r + (pR - r) * t + gr;
          data[k + 1] = gg + (pG - gg) * t + gr;
          data[k + 2] = b + (pB - b) * t + gr;
          data[k + 3] = 255;
          depth[y * pw + px] = z + (pZ - z) * t;
        }
        ymin = top;
        if (ymin <= 0) break;
      }
      pR = r;
      pG = gg;
      pB = b;
      pY = sy;
      pZ = z;
    }
    for (let y = ymin - 1; y >= 0; y -= 1) {
      const k = (y * pw + px) * 4;
      const gr = (grain[(px * 31 + y * 17) & 8191] as number) * 0.3;
      data[k] = (sky[y * 3] as number) + gr;
      data[k + 1] = (sky[y * 3 + 1] as number) + gr;
      data[k + 2] = (sky[y * 3 + 2] as number) + gr;
      data[k + 3] = 255;
    }
    if ((px & 15) === 0 && !(await breathe())) return false;
  }
  return true;
}

/** Point monde vers pixel du canvas. */
function proj(v: View, ratio: number, x: number, z: number, h: number): [number, number] {
  const S = v.s * ratio;
  return [(x - v.x0) * S, (z * v.sin - h * v.e * v.cos - v.y0) * S];
}

/** Vrai si le point est le terrain vu en ce pixel (pas caché par un relief plus proche). */
function seen(depth: Float32Array, pw: number, ph: number, X: number, Y: number, z: number, tol: number): boolean {
  const xi = Math.round(X);
  const yi = Math.round(Y);
  if (xi < 0 || yi < 0 || xi >= pw || yi >= ph) return false;
  const d = depth[yi * pw + xi] as number;
  return d > SKY_DEPTH && Math.abs(d - z) < tol;
}

/** Touches de pinceau : traits courts le long de la ligne de pente. */
async function paintStrokes(sc: PaintScene, g: Grids, ctx: CanvasRenderingContext2D, depth: Float32Array, pw: number, ph: number, breathe: () => Promise<boolean>): Promise<boolean> {
  const { hf, view: v, ratio } = sc;
  const S = v.s * ratio;
  const styles = [
    { color: 'rgba(255,255,255,0.75)', width: 1.1 },
    { color: 'rgba(132,156,200,0.26)', width: 1.1 },
    { color: 'rgba(70,96,152,0.32)', width: 1.2 },
    { color: 'rgba(214,204,190,0.55)', width: 1.1 },
    { color: 'rgba(46,44,56,0.55)', width: 1.3 },
  ];
  const paths = styles.map(() => new Path2D());
  const step = Math.max(3, 4.2 * ratio);
  let row = 0;
  for (let py = step / 2; py < ph; py += step) {
    row++;
    for (let px0 = step / 2; px0 < pw; px0 += step) {
      const px = px0 + (hash(row, px0 | 0, 1) - 0.5) * step;
      const pyj = py + (hash(row, px0 | 0, 2) - 0.5) * step;
      const xi = Math.round(px);
      const yi = Math.round(pyj);
      if (xi < 0 || yi < 0 || xi >= pw || yi >= ph) continue;
      const z = depth[yi * pw + xi] as number;
      if (z <= SKY_DEPTH) continue;
      const x = v.x0 + (xi + 0.5) / S;
      if (!hf.inBounds(x, z, 8) || z < sc.domain.minZ - 200) continue;
      // Touches de plus en plus rares vers les bords de la carte.
      if (hash(xi, yi, 9) < smoothstep(hf.half - 260, hf.half - 10, Math.abs(x))) continue;
      const q = Math.round((z + hf.half) / hf.cell) * hf.res + Math.round((x + hf.half) / hf.cell);
      if ((g.wood[q] as number) > 0.25 || (g.cov[q] as number) > 0.3 || g.water[q]) continue;
      const t = g.lit[q] as number;
      const rock = g.rock[q] as number;
      let style: number;
      if (rock > 0.45) style = t > 0.55 ? 3 : 4;
      else style = t > 0.8 ? 0 : t > 0.5 ? 1 : 2;
      if (style === 0 && hash(row, xi, 3) < 0.45) continue;
      // Ligne de pente, en trois pas (le trait suit le relief).
      const lenPx = (6 + hash(xi, yi, 4) * 9) * ratio * (rock > 0.45 ? 0.8 : 1);
      const lenM = lenPx / S / 3;
      let cx = x;
      let cz = z;
      const pts: [number, number][] = [];
      let ok = true;
      for (let k = 0; k < 3 && ok; k++) {
        const [gx, gz] = hf.gradient(cx, cz, 6);
        const sl = Math.hypot(gx, gz);
        let dx: number;
        let dzz: number;
        if (sl < 0.05) {
          dx = 1;
          dzz = 0.15;
        } else {
          dx = -gx / sl;
          dzz = -gz / sl;
        }
        if (k === 0) pts.push(proj(v, ratio, cx, cz, hf.height(cx, cz)));
        cx += dx * lenM;
        cz += dzz * lenM;
        const p = proj(v, ratio, cx, cz, hf.height(cx, cz));
        if (!seen(depth, pw, ph, p[0], p[1], cz, 30)) ok = false;
        else pts.push(p);
      }
      if (pts.length < 2) continue;
      const path = paths[style] as Path2D;
      const p0 = pts[0] as [number, number];
      path.moveTo(p0[0], p0[1]);
      if (pts.length === 3) {
        const p1 = pts[1] as [number, number];
        const p2 = pts[2] as [number, number];
        path.quadraticCurveTo(p1[0], p1[1], p2[0], p2[1]);
      } else {
        for (let k = 1; k < pts.length; k++) path.lineTo((pts[k] as [number, number])[0], (pts[k] as [number, number])[1]);
      }
    }
    if (!(await breathe())) return false;
  }
  ctx.lineCap = 'round';
  styles.forEach((s, k) => {
    ctx.strokeStyle = s.color;
    ctx.lineWidth = s.width * ratio;
    ctx.stroke(paths[k] as Path2D);
  });
  return true;
}

const TREE_DARK = ['#1f3b2f', '#27463a', '#2d4f36'];
const TREE_LIT = ['#4f7a5c', '#5b8463', '#6a8e66'];

/** Sapins en petites touches, du plus loin au plus proche. */
async function paintTrees(sc: PaintScene, g: Grids, ctx: CanvasRenderingContext2D, depth: Float32Array, pw: number, ph: number, breathe: () => Promise<boolean>): Promise<boolean> {
  const { hf, view: v, ratio } = sc;
  const S = v.s * ratio;
  const step = 6.5;
  const xa = Math.max(-hf.half + 4, v.x0);
  const xb = Math.min(hf.half - 4, v.x0 + pw / S);
  const za = Math.max(-hf.half + 4, sc.domain.minZ - 250);
  const zb = hf.half - 4;
  const base = Math.max(4.2, Math.min(11, v.s * 17)) * ratio;
  for (let iz = Math.floor(za / step); iz * step < zb; iz++) {
    const shadow = new Path2D();
    const dark = TREE_DARK.map(() => new Path2D());
    const lit = new Path2D();
    let any = false;
    for (let ix = Math.floor(xa / step); ix * step < xb; ix++) {
      const r1 = hash(ix, iz, 11);
      const x = ix * step + (hash(ix, iz, 12) - 0.5) * step * 0.9;
      const z = iz * step + (hash(ix, iz, 13) - 0.5) * step * 0.9;
      const q = Math.round((z + hf.half) / hf.cell) * hf.res + Math.round((x + hf.half) / hf.cell);
      const dens = (g.wood[q] as number) * (1 - smoothstep(hf.half - 300, hf.half - 20, Math.abs(x)));
      if (dens <= 0 || r1 > dens * 0.92) continue;
      const h = hf.height(x, z);
      const [X, Y] = proj(v, ratio, x, z, h);
      if (X < -10 || X > pw + 10 || Y < 0 || Y > ph + 20) continue;
      if (!seen(depth, pw, ph, X, Y - 1, z, 16)) continue;
      const th = base * (0.7 + 0.6 * hash(ix, iz, 14));
      const tw = th * 0.5;
      shadow.moveTo(X, Y);
      shadow.lineTo(X + tw * 1.3, Y + tw * 0.28);
      shadow.lineTo(X + tw * 0.2, Y + tw * 0.32);
      shadow.closePath();
      const d = dark[Math.floor(hash(ix, iz, 15) * 3)] as Path2D;
      d.moveTo(X, Y - th);
      d.lineTo(X + tw / 2, Y);
      d.lineTo(X - tw / 2, Y);
      d.closePath();
      lit.moveTo(X, Y - th);
      lit.lineTo(X - tw * 0.06, Y);
      lit.lineTo(X - tw / 2, Y);
      lit.closePath();
      any = true;
    }
    if (any) {
      ctx.fillStyle = 'rgba(70,92,140,0.32)';
      ctx.fill(shadow);
      dark.forEach((p, k) => {
        ctx.fillStyle = TREE_DARK[k] as string;
        ctx.fill(p);
      });
      ctx.fillStyle = TREE_LIT[iz % 3] as string;
      ctx.globalAlpha = 0.85;
      ctx.fill(lit);
      ctx.globalAlpha = 1;
    }
    if ((iz & 3) === 0 && !(await breathe())) return false;
  }
  return true;
}

/** Taille relative des bâtiments peints (0 : non peint). */
const HOUSE_SIZE: Partial<Record<BuildingKind, number>> = {
  chalet_accueil: 1.6,
  restaurant: 1.35,
  shop_rental: 1.05,
  ticket_office: 0.95,
  hotel: 2,
  residence: 1.7,
  ski_school: 1.05,
  first_aid: 0.9,
  snack_bar: 0.9,
  groomer_garage: 1.45,
};

/** Petits chalets du village et bâtiments de la station. */
function paintHouses(sc: PaintScene, g: Grids, ctx: CanvasRenderingContext2D, depth: Float32Array, pw: number, ph: number): void {
  const { hf, map, view: v, ratio } = sc;
  const onPiste = (x: number, z: number) => {
    const q = Math.round((z + hf.half) / hf.cell) * hf.res + Math.round((x + hf.half) / hf.cell);
    return (g.cov[q] as number) > 0.01;
  };
  const items: { x: number; z: number; size: number; garage: boolean; roof: number }[] = [];
  for (const it of placeVillageHouses(hf, map, onPiste, sc.lifts, sc.buildings)) items.push({ x: it.x, z: it.z, size: it.s, garage: false, roof: hash(Math.round(it.x), Math.round(it.z), 5) });
  for (const b of sc.buildings) {
    if (b.kind === 'parking') {
      paintParking(sc, ctx, b.x, b.z);
      continue;
    }
    const size = HOUSE_SIZE[b.kind];
    if (size) items.push({ x: b.x, z: b.z, size, garage: b.kind === 'groomer_garage', roof: hash(Math.round(b.x), Math.round(b.z), 6) });
  }
  items.sort((a, b) => a.z - b.z);
  const unit = Math.max(4.5, Math.min(12, v.s * 14)) * ratio;
  for (const it of items) {
    const h = hf.height(it.x, it.z);
    const [X, Y] = proj(v, ratio, it.x, it.z, h);
    if (!seen(depth, pw, ph, X, Y - 1, it.z, 20)) continue;
    const w = unit * it.size;
    const wallH = w * 0.38;
    // Ombre portée vers l'est.
    ctx.fillStyle = 'rgba(70,92,140,0.3)';
    ctx.beginPath();
    ctx.ellipse(X + w * 0.25, Y + w * 0.04, w * 0.7, w * 0.14, 0, 0, Math.PI * 2);
    ctx.fill();
    // Façade : bois, côté ouest éclairé.
    ctx.fillStyle = it.garage ? '#8b8f94' : '#9a6a40';
    ctx.fillRect(X - w / 2, Y - wallH, w, wallH);
    ctx.fillStyle = it.garage ? '#a9adb1' : '#b9875a';
    ctx.fillRect(X - w / 2, Y - wallH, w * 0.45, wallH);
    // Fenêtres.
    if (w > 6 * ratio) {
      ctx.fillStyle = '#f2d48a';
      const ww = Math.max(1, w * 0.1);
      for (let k = 0; k < 3; k++) ctx.fillRect(X - w * 0.32 + k * w * 0.26, Y - wallH * 0.62, ww, ww);
    }
    // Toit enneigé, rive sombre.
    ctx.beginPath();
    ctx.moveTo(X - w * 0.64, Y - wallH + 0.5);
    ctx.lineTo(X + w * 0.64, Y - wallH + 0.5);
    ctx.lineTo(X + w * 0.36, Y - wallH - w * 0.42);
    ctx.lineTo(X - w * 0.36, Y - wallH - w * 0.42);
    ctx.closePath();
    ctx.fillStyle = '#f7f8fb';
    ctx.fill();
    ctx.strokeStyle = it.garage ? '#4a4f55' : it.roof < 0.5 ? '#5a3720' : '#7c2f22';
    ctx.lineWidth = Math.max(1, w * 0.07);
    ctx.stroke();
    ctx.fillStyle = 'rgba(120,140,180,0.45)';
    ctx.beginPath();
    ctx.moveTo(X + w * 0.05, Y - wallH - w * 0.42);
    ctx.lineTo(X + w * 0.36, Y - wallH - w * 0.42);
    ctx.lineTo(X + w * 0.64, Y - wallH + 0.5);
    ctx.lineTo(X + w * 0.2, Y - wallH + 0.5);
    ctx.closePath();
    ctx.fill();
  }
}

/** Parking : aire grise tassée, posée sur le relief. */
function paintParking(sc: PaintScene, ctx: CanvasRenderingContext2D, x: number, z: number): void {
  const { hf, view: v, ratio } = sc;
  const r = 26;
  ctx.beginPath();
  for (let k = 0; k < 4; k++) {
    const a = (k / 4) * Math.PI * 2 + Math.PI / 4;
    const px = x + Math.cos(a) * r;
    const pz = z + Math.sin(a) * r * 0.7;
    const [X, Y] = proj(v, ratio, px, pz, hf.height(px, pz));
    if (k === 0) ctx.moveTo(X, Y);
    else ctx.lineTo(X, Y);
  }
  ctx.closePath();
  ctx.fillStyle = 'rgba(150,152,160,0.55)';
  ctx.fill();
}

/** Papier : bords légèrement teintés, comme une feuille imprimée. */
function paintPaper(ctx: CanvasRenderingContext2D, pw: number, ph: number): void {
  const grad = ctx.createRadialGradient(pw / 2, ph * 0.55, Math.min(pw, ph) * 0.35, pw / 2, ph * 0.55, Math.max(pw, ph) * 0.75);
  grad.addColorStop(0, 'rgba(240,226,196,0)');
  grad.addColorStop(1, 'rgba(214,190,150,0.28)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, pw, ph);
}

/** Peint le panorama ; null si le travail a été annulé. */
export async function paintPanorama(sc: PaintScene, job: PaintJob, progress: (f: number) => void): Promise<Painted | null> {
  const t0 = performance.now();
  const breathe = makeBreather(job);
  const pw = Math.max(1, Math.round(sc.view.w * sc.ratio));
  const ph = Math.max(1, Math.round(sc.view.h * sc.ratio));
  progress(0.02);
  const g = await buildGrids(sc, breathe);
  if (!g) return null;
  progress(0.3);
  const far = buildFarGrid(sc, pw);
  if (!(await breathe())) return null;
  const img = new ImageData(pw, ph);
  const depth = new Float32Array(pw * ph).fill(SKY_DEPTH);
  if (!(await marchColumns(sc, g, far, img, depth, breathe))) return null;
  progress(0.65);
  const canvas = document.createElement('canvas');
  canvas.width = pw;
  canvas.height = ph;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.putImageData(img, 0, 0);
  if (!(await paintStrokes(sc, g, ctx, depth, pw, ph, breathe))) return null;
  progress(0.8);
  if (!(await paintTrees(sc, g, ctx, depth, pw, ph, breathe))) return null;
  progress(0.95);
  paintHouses(sc, g, ctx, depth, pw, ph);
  paintPaper(ctx, pw, ph);
  progress(1);
  return { canvas, depth, pw, ph, ratio: sc.ratio, ms: performance.now() - t0 };
}

/** Vrai si un point 3D (hauteur réelle, non exagérée) est visible sur le panorama. */
export function visibleOnPanorama(p: Painted, v: View, x: number, z: number, h: number, tol = 22): boolean {
  const [X, Y] = proj(v, p.ratio, x, z, h);
  const xi = Math.round(X);
  const yi = Math.round(Y);
  if (xi < 0 || yi < 0 || xi >= p.pw || yi >= p.ph) return false;
  // Un relief plus proche (z plus grand) couvre le point : il est caché.
  return (p.depth[yi * p.pw + xi] as number) < z + tol;
}
